
import React, { useState, useCallback } from 'react';
import { GalleryImage, SiteContent } from '../types';
import { Trash2, Loader2, Upload, X } from 'lucide-react';
import { uploadService } from '../services/uploadService';
import { MediaRenderer } from './MediaRenderer';

interface GalleryProps {
  data: SiteContent['gallery'];
  isEditable: boolean;
  onUpdate: (data: SiteContent['gallery']) => void;
}

const Gallery: React.FC<GalleryProps> = ({ data, isEditable, onUpdate }) => {
  const [filter, setFilter] = useState('all');
  const [isUploading, setIsUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);

  const filtered = filter === 'all' 
    ? (data?.images || []) 
    : (data?.images || []).filter(img => img.category === filter);

  const handleUpload = async () => {
    try {
      setIsUploading(true);
      const base64 = await uploadService.selectAndUpload();
      const newImage: GalleryImage = {
        id: `g-${Date.now()}`,
        url: base64,
        caption: 'New Discovery',
        category: (filter !== 'all' ? filter : 'nature') as GalleryImage['category']
      };
      onUpdate({ ...data, images: [...(data.images || []), newImage] });
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setIsUploading(false);
    }
  };

  const removeImage = (id: string) => onUpdate({ ...data, images: data.images.filter(img => img.id !== id) });

  return (
    <section id="gallery" className="relative py-32 bg-slate-950 overflow-hidden scroll-mt-20">
      <div className="container mx-auto px-6 relative z-10">
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between mb-16 gap-8">
          <div className="max-w-xl">
            {isEditable ? (
              <div className="p-8 border-2 border-dashed border-white/10 rounded-3xl bg-white/5 space-y-6">
                <input 
                  value={data.title}
                  onChange={e => onUpdate({...data, title: e.target.value})}
                  className="w-full bg-transparent text-3xl font-bold font-serif italic text-white focus:outline-none"
                />
                <button onClick={handleUpload} className="bg-white text-slate-950 px-8 py-4 rounded-full font-bold text-[10px] uppercase tracking-luxury flex items-center gap-3">
                  {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Add to Collection
                </button>
              </div>
            ) : (
              <>
                <span className="text-sky-400 font-bold tracking-luxury uppercase text-[10px] mb-8 block">Visual Narrative</span>
                <h2 className="text-4xl md:text-5xl font-bold font-serif leading-none tracking-tighter text-white">
                  Expedition <span className="italic font-light block mt-4 text-sky-400/90">Archives</span>
                </h2>
              </>
            )}
          </div>
          
          <div className="flex flex-row flex-nowrap gap-2 w-full md:w-auto items-center justify-start md:justify-end pb-4 md:pb-0 overflow-x-auto no-scrollbar scroll-smooth">
            {['all', 'nature', 'culture', 'adventure'].map(cat => (
              <button 
                key={cat}
                onClick={() => setFilter(cat)}
                className={`flex-shrink-0 px-5 py-2 rounded-full text-[8px] font-extrabold uppercase tracking-widest transition-all border duration-300 ${filter === cat ? 'bg-white text-slate-950 border-white shadow-lg' : 'bg-transparent border-white/10 text-white/40 hover:text-white hover:border-white/30'}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 auto-rows-[300px]">
          {filtered.map((img, idx) => (
            <div 
              key={img.id} 
              className={`group relative overflow-hidden rounded-[3rem] cursor-pointer ${[
                'md:col-span-8 md:row-span-2', 'md:col-span-4 md:row-span-1', 'md:col-span-4 md:row-span-2'
              ][idx % 3] || 'md:col-span-4'}`}
              onClick={() => setSelectedImage(img)}
            >
              <MediaRenderer 
                src={img.url} 
                className="w-full h-full object-cover grayscale opacity-40 group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-110 transition-all duration-[1.5s]" 
                alt={img.caption} 
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent flex flex-col justify-end p-12 opacity-0 group-hover:opacity-100 transition-opacity">
                 <span className="text-[10px] font-bold uppercase tracking-luxury text-sky-400 mb-2">{img.category}</span>
                 <p className="text-3xl font-serif italic text-white">{img.caption}</p>
                 {isEditable && <button onClick={(e) => { e.stopPropagation(); removeImage(img.id); }} className="absolute top-6 right-6 bg-red-500 p-3 rounded-xl text-white"><Trash2 className="w-4 h-4" /></button>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedImage && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center animate-in fade-in" onClick={() => setSelectedImage(null)}>
          <div className="absolute inset-0 bg-slate-950/98 backdrop-blur-3xl"></div>
          <button className="absolute top-10 right-10 text-white/50 hover:text-white p-4 z-[110]" onClick={() => setSelectedImage(null)}><X className="w-10 h-10" /></button>
          <div className="relative z-[105] text-center p-8">
            <MediaRenderer src={selectedImage.url} className="max-w-[90vw] max-h-[70vh] rounded-[2rem] shadow-2xl mb-8" alt={selectedImage.caption} />
            <h3 className="text-3xl md:text-5xl font-serif italic text-white">{selectedImage.caption}</h3>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
